import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Watch, Glasses, Smartphone, Cpu, Plus, Trash2, Radar, CheckCircle2, XCircle,
  Navigation, MapPin, Loader2, Wifi, Activity, Vibrate,
} from "lucide-react";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-cobalt";

const KINDS = [
  { key: "meta_glasses", label: "Meta Smart Glasses", icon: Glasses },
  { key: "apple_watch", label: "Apple Watch", icon: Watch },
  { key: "android_wear", label: "Android Wear", icon: Smartphone },
  { key: "other", label: "Other device", icon: Cpu },
];
const kindMeta = (k) => KINDS.find((x) => x.key === k) || KINDS[3];

// Human-readable label for durable platform events surfaced on the wearable HUD.
const EVENT_LABEL = {
  "dispatch.offered": "New mission offer",
  "dispatch.reoffered": "Mission re-offered",
  "dispatch.accepted": "Mission accepted",
  "dispatch.en_route": "Marked en route",
  "dispatch.on_scene": "Arrived on scene",
  "dispatch.completed": "Mission completed",
  "geofence.violation": "Geofence alert",
  "mission.upload": "Evidence uploaded",
  "pilot.rated": "You were rated",
};

const NEXT = {
  accepted: ["en_route", "En route", Navigation],
  en_route: ["on_scene", "On scene", MapPin],
  on_scene: ["completed", "Complete", CheckCircle2],
};

export default function WearablesPanel() {
  const [devices, setDevices] = useState([]);
  const [feed, setFeed] = useState({ offers: [], active: [], events: [], availability: null });
  const [kind, setKind] = useState("meta_glasses");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const loadDevices = useCallback(async () => {
    try { const { data } = await api.get("/wearables/devices"); setDevices(data); } catch { /* ignore */ }
  }, []);
  const loadFeed = useCallback(async () => {
    try { const { data } = await api.get("/wearables/feed"); setFeed(data); } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadDevices(); loadFeed();
    timer.current = setInterval(loadFeed, 8000);
    return () => clearInterval(timer.current);
  }, [loadDevices, loadFeed]);

  const register = async () => {
    setBusy(true);
    try {
      await api.post("/wearables/register", { device_kind: kind, device_label: label || null });
      setLabel("");
      toast.success("Wearable connected");
      loadDevices();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const act = async (dispatch_id, action, ok) => {
    try {
      await api.post("/wearables/action", { dispatch_id, action });
      toast.success(ok);
      loadFeed();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const online = feed.availability?.online || feed.availability === "online";

  return (
    <div className="mt-6 space-y-6" data-testid="tab-wearables">
      {/* Connected devices */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-heading font-semibold text-navy">Connected Wearables</h2>
          <span className="text-xs font-medium text-cobalt bg-cobalt/10 px-2 py-0.5 rounded-full">Hands-free field ops</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="wearable-device-list">
          {devices.length === 0 ? (
            <div className="sm:col-span-2 bg-white rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400" data-testid="wearable-empty">
              No devices connected yet. Pair a wearable below to receive mission offers on your wrist or glasses.
            </div>
          ) : devices.map((d) => {
            const M = kindMeta(d.device_kind); const Icon = M.icon;
            return (
              <div key={d.wearable_id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3 shadow-sm" data-testid={`wearable-device-${d.wearable_id}`}>
                <div className="h-11 w-11 rounded-xl bg-navy flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-cyan-400" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-navy text-sm truncate">{d.device_label || M.label}</div>
                  <div className="text-xs text-slate-400">{M.label} · connected {new Date(d.created_at).toLocaleDateString()}</div>
                </div>
                <span className="flex items-center gap-1 text-xs font-medium text-emerald-600"><Wifi className="w-3.5 h-3.5" /> Paired</span>
              </div>
            );
          })}
        </div>

        {/* Pair a device */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div className="text-sm font-medium text-navy mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-cobalt" /> Pair a new wearable</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {KINDS.map((k) => {
              const Icon = k.icon; const on = kind === k.key;
              return (
                <button key={k.key} onClick={() => setKind(k.key)} data-testid={`wearable-kind-${k.key}`}
                  className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 text-xs font-medium transition-all ${on ? "border-cobalt bg-cobalt-light text-cobalt" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                  <Icon className="w-5 h-5" /> {k.label}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Device nickname (optional) — e.g. Field glasses" className={inputCls} data-testid="wearable-label-input" />
            <button onClick={register} disabled={busy} data-testid="wearable-pair-btn"
              className="shrink-0 flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-cobalt text-white text-sm font-semibold hover:bg-cobalt-hover disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Pair device
            </button>
          </div>
        </div>
      </div>

      {/* Wearable HUD preview */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-navy flex items-center gap-2"><Radar className="w-4 h-4 text-cobalt" /> Wearable HUD Preview</h2>
          <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${loading ? "bg-slate-100 text-slate-400" : online ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`} data-testid="wearable-hud-status">
            <span className={`w-2 h-2 rounded-full ${loading ? "bg-slate-300 animate-pulse" : online ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} /> {loading ? "Syncing" : online ? "Live" : "Offline"}
          </span>
        </div>
        <div className="rounded-2xl bg-navy text-white p-5 shadow-lg" data-testid="wearable-hud">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
          ) : (
            <div className="space-y-4">
              {/* Offers */}
              {feed.offers.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-cyan-300/80 mb-2 flex items-center gap-1.5"><Vibrate className="w-3.5 h-3.5" /> Incoming offers</div>
                  {feed.offers.map((o) => (
                    <div key={o.dispatch_id} className="rounded-xl bg-white/5 border border-cyan-400/20 p-3 mb-2 flex items-center justify-between gap-2" data-testid={`wearable-offer-${o.dispatch_id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{[o.location_city, o.location_state].filter(Boolean).join(", ") || "New mission"}</div>
                        <div className="text-xs text-slate-300">{o.case_number}{o.fee ? ` · $${o.fee}` : ""}</div>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        <button onClick={() => act(o.dispatch_id, "accept", "Accepted from wearable")} data-testid={`wearable-accept-${o.dispatch_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-cyan-400 text-navy text-xs font-bold hover:bg-cyan-300"><CheckCircle2 className="w-3.5 h-3.5" /> Accept</button>
                        <button onClick={() => act(o.dispatch_id, "decline", "Declined from wearable")} data-testid={`wearable-decline-${o.dispatch_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs hover:bg-white/20"><XCircle className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Active mission */}
              {feed.active.length > 0 ? feed.active.map((j) => {
                const next = NEXT[j.status];
                return (
                  <div key={j.dispatch_id} className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid={`wearable-active-${j.dispatch_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{[j.location_city, j.location_state].filter(Boolean).join(", ") || "Active mission"}</div>
                        <div className="text-xs text-cyan-300 capitalize">{j.status?.replace("_", " ")}</div>
                      </div>
                      {next && (
                        <button onClick={() => act(j.dispatch_id, next[0], next[1])} data-testid={`wearable-advance-${j.dispatch_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-400 text-navy text-xs font-bold hover:bg-cyan-300 shrink-0">
                          {(() => { const I = next[2]; return <I className="w-3.5 h-3.5" />; })()} {next[1]}
                        </button>
                      )}
                    </div>
                  </div>
                );
              }) : feed.offers.length === 0 && (
                <div className="text-center py-6 text-slate-400 text-sm" data-testid="wearable-hud-idle">
                  <Radar className="w-8 h-8 mx-auto text-slate-500 mb-2" />
                  Standing by for missions. Offers appear here on your paired devices in real time.
                </div>
              )}

              {/* Event stream */}
              {feed.events.length > 0 && (
                <div className="border-t border-white/10 pt-3">
                  <div className="text-xs uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Recent activity</div>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto" data-testid="wearable-event-stream">
                    {feed.events.slice(0, 12).map((ev, i) => (
                      <div key={ev.event_id || i} className="flex items-center justify-between text-xs text-slate-300" data-testid={`wearable-event-${i}`}>
                        <span>{EVENT_LABEL[ev.type] || ev.type}</span>
                        <span className="text-slate-500">{ev.at ? new Date(ev.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <p className="mt-2 text-xs text-slate-400">Actions taken here route through the same dispatch pipeline your paired glasses and watch use — accept, navigate, and update missions completely hands-free.</p>
      </div>
    </div>
  );
}
